import 'intersection-observer'
import React, {PureComponent, createRef} from 'react'
import {compose} from 'redux'
import {connect} from 'react-redux'
import {retry} from '@rambler-id/promise'
import {reachGoal, trackEvent} from 'utils/metrics'
import {GENERATE_CHALLENGE} from '@rambler-id/transport'
import {apiV3} from 'utils/transport'
import {sentryReact as Sentry} from '@rambler-id/sentry'
import {createCredential, Errors} from '@rambler-id/webauthn'
import {AddIcon, providePopup} from 'rambler-ui'
import {throwError, showSnackbar} from 'containers/App/actions'
import {SuspensePopup} from 'components/SuspensePopup'
import InternalHeader from 'components/InternalHeader'
import ProfileSection from 'components/ProfileSection'
import ProfileItem from 'components/ProfileItem'
import RowButton from 'components/RowButton'
import WebAuthnIcon from 'components/icons/WebAuthnIcon'
import WebAuthnKey from './WebAuthnKey'

const ConfirmWebAuthn = React.lazy(
  retry(() =>
    import(
      /* webpackChunkName: "confirm-webauthn-with-password" */ 'containers/ConfirmWebAuthn'
    )
  )
)

const DeleteWebAuthn = React.lazy(
  retry(() =>
    import(
      /* webpackChunkName: "delete-webauthn-with-password" */ 'containers/DeleteWebAuthn'
    )
  )
)

class WebAuthn extends PureComponent {
  bindButton = createRef()

  intersectionObserver = new window.IntersectionObserver(
    ([entry]) => {
      if (entry && entry.intersectionRatio === 1) {
        trackEvent('bind_webauthn_form', {bind_webauthn_show: true})
        reachGoal('bind_webauthn_show')
        this.intersectionObserver.disconnect()
      }
    },
    {root: null, rootMargin: '0px', threshold: 1.0}
  )

  async componentDidMount() {
    try {
      if (this.bindButton.current) {
        this.intersectionObserver.observe(this.bindButton.current)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  componentWillUnmount() {
    this.intersectionObserver.disconnect()
  }

  createCredential = async () => {
    const {
      isPrimaryMailBlocked,
      showBlockedEmailSnackbar,
      info,
      display,
      throwError
    } = this.props

    if (isPrimaryMailBlocked) {
      showBlockedEmailSnackbar()

      return
    }

    trackEvent('bind_webauthn_form', {bind_webauthn_try: true})
    reachGoal('bind_webauthn_try')

    try {
      const {ticketId, challenge} = await apiV3(`/legacy/${GENERATE_CHALLENGE}`)
      const decodedCredential = await createCredential(
        ticketId,
        challenge,
        info.chainId.default,
        display.displayName
      )

      this.confirmCredential(decodedCredential, display, info.chainId.default)
    } catch (error) {
      if (error.message === Errors.CREATION_CANCELLED) {
        trackEvent('bind_webauthn_form', {bind_webauthn_cancel: true})
        reachGoal('bind_webauthn_cancel')

        return
      }

      if (error.message === Errors.CREATION_FAILED) {
        trackEvent('bind_webauthn_form', {bind_webauthn_create: true})
        reachGoal('bind_webauthn_fail_create')
      }

      if (
        error.message === Errors.CREATION_DECODING_FAILED ||
        error.message === Errors.CREATION_PARSING_FAILED
      ) {
        trackEvent('bind_webauthn_form', {bind_webauthn_fail_parse: true})
        reachGoal('bind_webauthn_fail_parse')
      } else {
        trackEvent('bind_webauthn_form', {bind_webauthn_fail_api: true})
        reachGoal('bind_webauthn_fail_api')
      }

      Sentry.captureException(error)
      throwError(error)
    }
  }

  confirmCredential = async (decodedCredential, display, chainId) => {
    try {
      await this.props.openPopup((onResolve, onReject) => (
        <SuspensePopup fallback={<div />}>
          {(props) => (
            <ConfirmWebAuthn
              onResolve={onResolve}
              onReject={onReject}
              decodedCredential={decodedCredential}
              display={display}
              chainId={chainId}
              {...props}
            />
          )}
        </SuspensePopup>
      )).closed
    } catch (error) {
      trackEvent('bind_webauthn_form', {bind_webauthn_cancel: true})
      reachGoal('bind_webauthn_cancel')
    }
  }

  deleteCredential = async (credential) => {
    try {
      await this.props.openPopup((onResolve, onReject) => (
        <SuspensePopup fallback={<div />}>
          {(props) => (
            <DeleteWebAuthn
              onResolve={onResolve}
              onReject={onReject}
              credential={credential}
              {...props}
            />
          )}
        </SuspensePopup>
      )).closed
    } catch (error) {} // eslint-disable-line no-empty
  }

  render() {
    const {webAuthnAvailable, webAuthnKeys} = this.props

    return (
      <ProfileSection
        section="webauthn"
        color="#536dfe"
        icon={<WebAuthnIcon />}>
        <InternalHeader>
          <h2>Электронный ключ</h2>
          <p>
            Электронный ключ - это удобный способ входа в ваш профиль с помощью
            отпечатка пальца или по Face ID. Этот ключ зашифрован и надежно
            хранится на вашем устройстве. Его нельзя украсть, поэтому данный
            способ входа безопаснее обычного пароля. Для каждого вашего
            устройства создается новый уникальный ключ, все они перечислены
            ниже.
          </p>
        </InternalHeader>
        {!!webAuthnKeys &&
          webAuthnKeys.length > 0 &&
          webAuthnKeys.map((key) => (
            <WebAuthnKey
              key={key.publicId || key.id}
              title={key.properties.title}
              browser={key.properties.browser}
              deleteCredential={() => this.deleteCredential(key)}
            />
          ))}
        {webAuthnAvailable && (
          <ProfileItem
            rightIcon={
              <RowButton
                label="Создать электронный ключ"
                onClick={this.createCredential}>
                <AddIcon />
              </RowButton>
            }
            cerberId="profile::webauthn::create_webauthn">
            <button
              type="button"
              data-anchor
              tabIndex="0"
              ref={this.bindButton}
              onClick={this.createCredential}>
              Создать электронный ключ
            </button>
          </ProfileItem>
        )}
      </ProfileSection>
    )
  }
}

function mapDispatchToProps(dispatch) {
  return {
    throwError: (error) =>
      dispatch(
        throwError(
          'При привязке ключа произошла ошибка. Попробуйте снова',
          error
        )
      ),
    showBlockedEmailSnackbar: () => {
      dispatch(
        showSnackbar(
          'Вход в ваш профиль с помощью электронной почты заблокирован. Вам необходимо сменить пароль. Для восстановления доступа выйдите из профиля и войдите в него, указав в качестве логина свою почту',
          {type: 'main', autoCloseDuration: 10000}
        )
      )
    }
  }
}

const withConnect = connect(null, mapDispatchToProps)

export default compose(withConnect, providePopup)(WebAuthn)
